import React from 'react';
import { 
    List, 
    Datagrid, 
    TextField, 
    Edit, 
    Create, 
    EditButton, 
    DeleteButton, 
    CloneButton, 
    SimpleForm, 
    TextInput, 
    NumberInput, 
    SelectInput, 
    ReferenceInput,
    ReferenceArrayInput, 
    SelectArrayInput,
    AutocompleteArrayInput,
    AutocompleteInput,
    DateTimeInput,
    BooleanInput,
    required,
    Toolbar,
    SaveButton,
    ListButton,
    TopToolbar,
    Pagination,
    ChipField,
    SingleFieldList,
    ReferenceManyField,
    FunctionField,
    BooleanField,
    NumberField,
    DateField,
    useRecordContext,
    useListContext,
    Button,
    useDataProvider,
    useNotify,
    useRefresh,
    Filter,
    SearchInput,
    SelectArrayInput as FilterSelectArrayInput,
    ReferenceField,
    useReferenceArrayInputContext,
    useLoading,
} from 'react-admin';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useForm, useFormState } from 'react-final-form';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import './autocomplete-chips.css';
// Debug logger: prints model and account manager names for current rows
const SubscriptionPlanDebugLog = () => {
    const { data, ids, isLoading } = useListContext();
    React.useEffect(() => {
        if (isLoading) return;
        try {
            const rows = (ids || []).map(id => {
                const row = data && data[id];
                return row ? {
                    id: row.id,
                    subscription_model_name: row.subscription_model_name,
                    account_manager_name: row.account_manager_name
                } : null;
            }).filter(Boolean);
        } catch (e) {
        }
    }, [data, ids, isLoading]);
    return null;
};


const SubscriptionPlanPagination = props => <Pagination rowsPerPageOptions={[100, 50, 25, 10]} {...props} />;

const subscriptionPeriodChoices = [
    { id: 'daily', name: 'Daily' },
    { id: 'monthly', name: 'Monthly' },
    { id: 'annually', name: 'Annually' },
];

// Custom component for usage percentage display (total_usage / effective_cycle_limit)
const UsagePercentageField = () => {
    const record = useRecordContext();
    const total = Number(record?.total_usage ?? 0);
    const limit = Number(record?.effective_cycle_limit ?? 0);
    const percentage = limit > 0 ? (total / limit) * 100 : 0;
    const color = percentage > 80 ? 'error' : percentage > 60 ? 'warning' : 'primary';
    
    return (
        <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            minWidth: '100px'
        }}>
            <div style={{
                flex: 1,
                height: '8px',
                backgroundColor: '#e0e0e0',
                borderRadius: '4px',
                overflow: 'hidden'
            }}>
                <div style={{
                    width: `${Math.min(percentage, 100)}%`,
                    height: '100%',
                    backgroundColor: color === 'error' ? '#f44336' : color === 'warning' ? '#ff9800' : '#4caf50',
                    transition: 'width 0.3s ease'
                }} />
            </div>
            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                {percentage.toFixed(1)}%
            </span>
        </div>
    );
};

// Custom component for validity status
const ValidityStatusField = () => {
    const record = useRecordContext();
    const isValid = record.is_valid;
    const isActive = record.is_active;
    const now = new Date();
    const validFrom = record.valid_from ? new Date(record.valid_from) : null;
    const validUntil = record.valid_until ? new Date(record.valid_until) : null;

    let status = 'Inactive';
    let color = '#757575';

    if (!isActive) {
        status = 'Inactive';
        color = '#757575';
    } else if (validFrom && validFrom > now) {
        status = 'Scheduled';
        color = '#1976d2';
    } else if (validUntil && validUntil < now) {
        status = 'Expired';
        color = '#f44336';
    } else if (isValid) {
        status = 'Valid';
        color = '#4caf50';
    } else {
        // Fallback for edge cases
        status = 'Valid';
        color = '#4caf50';
    }

    return (
        <span style={{ 
            color, 
            fontWeight: 'bold',
            fontSize: '12px',
            padding: '2px 8px',
            borderRadius: '12px',
            backgroundColor: `${color}20`,
            border: `1px solid ${color}40`
        }}>
            {status}
        </span>
    );
};

// Custom action buttons
const AddCycleButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    
    const handleAddCycle = () => {
        dataProvider.custom('subscription-plans', 'add_cycle', {
            id: record.id
        }).then(() => {
            notify('Cycle added successfully', 'success');
            refresh();
        }).catch(() => {
            notify('Failed to add cycle', 'error');
        });
    };
    
    return (
        <Button 
            label="Add Cycle" 
            onClick={handleAddCycle}
            disabled={!record.can_add_cycle}
            size="small"
        />
    );
};

const ResetCyclesButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    
    const handleResetCycles = () => {
        dataProvider.custom('subscription-plans', 'reset_cycles', {
            id: record.id
        }).then(() => {
            notify('Cycles reset successfully', 'success');
            refresh();
        }).catch(() => {
            notify('Failed to reset cycles', 'error');
        });
    };
    
    return (
        <Button 
            label="Reset Cycles" 
            onClick={handleResetCycles}
            size="small"
        />
    );
};

const SubscriptionPlanFilter = (props) => (
    <Filter {...props}>
        <SearchInput source="search" alwaysOn />
        <ReferenceInput 
            source="subscription_model" 
            reference="subscription-models"
            label="Subscription Model"
        >
            <SelectInput optionText="name" />
        </ReferenceInput>
        <SelectInput 
            source="is_active" 
            choices={[
                { id: true, name: 'Active' },
                { id: false, name: 'Inactive' }
            ]}
            label="Status"
        />
        <SelectInput 
            source="subscription_period_type" 
            choices={subscriptionPeriodChoices}
            label="Period Type"
        />
    </Filter>
);

export const SubscriptionPlanList = props => (
    <List {...props} pagination={<SubscriptionPlanPagination />} filters={<SubscriptionPlanFilter />}>
        <React.Fragment>
            <SubscriptionPlanDebugLog />
            <Datagrid rowClick="edit">
            <TextField source="id" />
            <TextField source="name" />
            <TextField source="account_manager_name" label="Account Manager" />
            <TextField source="code" />
            <TextField source="subscription_model_name" label="Model" />
            <NumberField source="effective_cycle_limit" label="Cycle Limit" />
            <NumberField source="total_usage" label="Used" />
            <FunctionField 
                label="Remaining" 
                render={record => {
                    if (record == null) return '';
                    const backend = record.remaining;
                    if (backend !== undefined && backend !== null) return backend;
                    const limit = Number(record?.effective_cycle_limit ?? 0);
                    const used = Number(record?.total_usage ?? 0);
                    const baseRemaining = Math.max(0, limit - used);
                    const isAvailable = Boolean(record?.is_active) && Boolean(record?.is_valid);
                    return isAvailable ? baseRemaining : 0;
                }}
            />
            <UsagePercentageField label="Usage" />
            <BooleanField source="is_active" label="Active" />
            <DateField source="valid_from" label="Valid From" showTime />
            <DateField source="renew_date" label="Renew Date" showTime />
            <DateField source="valid_until" label="Valid Until" showTime />
            <PlanSitesField label="Sites" />
            <EditButton />
            <DeleteButton mutationMode={"pessimistic"} />
            <CloneButton />
            </Datagrid>
        </React.Fragment>
    </List>
);

// Display site names from list payload only (no background fetch)
const PlanSitesField = () => {
    const record = useRecordContext();
    const sites = Array.isArray(record && record.effective_sites) ? record.effective_sites : [];
    if (!sites.length) return <span>—</span>;

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {sites.map((s, idx) => {
                const name = s && (s.name || s.sub_name || String(s));
                return (
                    <span key={`${name}-${idx}`} style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '16px',
                        backgroundColor: '#e0e0e0',
                        color: '#424242',
                    }}>
                        {name}
                    </span>
                );
            })}
        </div>
    );
};

// Autocomplete for Associated Sites with a loader icon visually
// aligned to the right edge of the autocomplete input, using the
// same global loading flag as the navbar plus the field loading state.
const AssociatedSitesAutocompleteInput = (props) => {
    const { loading: refLoading } = useReferenceArrayInputContext();
    const globalLoading = useLoading();
    const loading = !!(globalLoading || refLoading);

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <AutocompleteArrayInput
                {...props}
                optionText="name"
                filterToQuery={searchText => ({ q: searchText })}
                options={{
                    suggestionsContainerProps: {
                        modifiers: {
                            flip: { enabled: false },
                        },
                    },
                }}
            />
            {loading && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        zIndex: 2,
                    }}
                >
                    <CircularProgress size={18} />
                </div>
            )}
        </div>
    );
};

export const SubscriptionPlanEdit = props => (
    <Edit title={<SubscriptionPlanTitle />} actions={<SubscriptionPlanEditActions />} {...props}>
        <SimpleForm toolbar={<SubscriptionPlanFormToolbar />}>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <AddCycleButton />
                <ResetCyclesButton />
            </div>
            <SubscriptionPlanPrefillFromModel />
            <SubscriptionPlanAutoRenewDate />
            <TextInput source="id" disabled />
            <ReferenceInput 
                source="subscription_model" 
                reference="subscription-models"
                label="Subscription Model"
            >
                <SelectInput optionText="name" />
            </ReferenceInput>
            <TextInput source="name" validate={required()} />
            <TextInput source="description" multiline rows={3} />
            <TextInput 
                source="code" 
                label="Code"
                validate={required()} 
                format={value => (value ? String(value).replace(/^#/, '') : value)}
                parse={value => {
                    const cleaned = value ? value.toUpperCase().replace(/[^A-Z0-9]/g, '') : value;
                    return cleaned ? `#${cleaned}` : cleaned;
                }}
                helperText={false}
                options={{
                    InputProps: {
                        startAdornment: (
                            <InputAdornment position="start" style={{ marginRight: 0, paddingRight: 0 }}>#</InputAdornment>
                        )
                    }
                }}
            />
            <CycleLimitOverrideInput />
            <NumberInput 
                source="cycle_count" 
                min={0}
                label="Current Cycle Count"
            />
            {/* total_usage is computed; no input on create */}
            <PeriodTypeOverrideInput />
            <ReferenceArrayInput 
                source="sites" 
                reference="sites"
                label="Associated Sites"
            >
                <AssociatedSitesAutocompleteInput />
            </ReferenceArrayInput>
            <BooleanInput source="is_active" label="Active" />
            <DateTimeInput source="valid_from" label="Valid From" />
            <DateTimeInput source="valid_until" label="Valid Until" />
            <DateTimeInput source="renew_date" label="Renew Date" />
            <TextInput source="account_name" label="Account Name" />
            <ReferenceInput 
                source="account_manager" 
                reference="users"
                label="Account Manager"
                allowEmpty
                perPage={100}
                format={v => (v === undefined || v === null ? null : v)}
                parse={v => (v === undefined || v === null || v === '' ? null : v)}
            >
                <AutocompleteInput 
                    optionText={record => {
                        if (!record) return '';
                        return record.full_name || record.email || String(record.id || '');
                    }}
                    optionValue="id"
                    filterToQuery={searchText => ({ 
                        q: searchText 
                    })}
                    options={{
                        suggestionsContainerProps: {
                            modifiers: {
                                flip: { enabled: false },
                            },
                        },
                    }}
                />
            </ReferenceInput>
            
        </SimpleForm>
    </Edit>
);

export const SubscriptionPlanCreate = props => (
    <Create {...props}>
        <SimpleForm toolbar={<SubscriptionPlanFormToolbar />} redirect="list" initialValues={{ is_active: false }}>
            <SubscriptionPlanPrefillFromModel />
            <SubscriptionPlanAutoRenewDate />
            <ReferenceInput 
                source="subscription_model" 
                reference="subscription-models"
                label="Subscription Model"
                validate={required()}
            >
                <SelectInput optionText="name" />
            </ReferenceInput>
            <TextInput source="name" validate={required()} />
            <TextInput source="description" multiline rows={3} />
            <TextInput 
                source="code" 
                label="Code"
                validate={required()} 
                format={value => (value ? String(value).replace(/^#/, '') : value)}
                parse={value => {
                    const cleaned = value ? value.toUpperCase().replace(/[^A-Z0-9]/g, '') : value;
                    return cleaned ? `#${cleaned}` : cleaned;
                }}
                helperText={false}
                options={{
                    InputProps: {
                        startAdornment: (
                            <InputAdornment position="start" style={{ marginRight: 0, paddingRight: 0 }}>#</InputAdornment>
                        )
                    }
                }}
            />
            <CycleLimitOverrideInput />
            {/* cycle_count defaults to 0 on create; no input needed */}
            <PeriodTypeOverrideInput />
            <ReferenceArrayInput 
                source="sites" 
                reference="sites"
                label="Associated Sites"
            >
                <AssociatedSitesAutocompleteInput />
            </ReferenceArrayInput>
            <BooleanInput source="is_active" label="Active" />
            <DateTimeInput source="valid_from" label="Valid From" />
            <DateTimeInput source="valid_until" label="Valid Until" />
            <DateTimeInput source="renew_date" label="Renew Date" />
            <TextInput source="account_name" label="Account Name" />
            <ReferenceInput 
                source="account_manager" 
                reference="users"
                label="Account Manager"
                allowEmpty
                perPage={100}
                format={v => (v === undefined || v === null ? null : v)}
                parse={v => (v === undefined || v === null || v === '' ? null : v)}
            >
                <AutocompleteInput 
                    optionText={record => {
                        if (!record) return '';
                        return record.full_name || record.email || String(record.id || '');
                    }}
                    optionValue="id"
                    filterToQuery={searchText => ({ 
                        q: searchText 
                    })}
                    options={{
                        suggestionsContainerProps: {
                            modifiers: {
                                flip: { enabled: false },
                            },
                        },
                    }}
                />
            </ReferenceInput>
        </SimpleForm>
    </Create>
);

const SubscriptionPlanTitle = ({ record }) => {
    return <span>Subscription Plan {record ? `"${record.name}"` : ''}</span>;
};

// Prefill plan fields from selected subscription model
const SubscriptionPlanPrefillFromModel = () => {
    const form = useForm();
    const { values } = useFormState();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [lastAppliedModelId, setLastAppliedModelId] = React.useState(null);

    React.useEffect(() => {
        const modelId = values && values.subscription_model;
        // Do not auto-prefill when editing an existing plan
        // Only prefill on create (no id yet)
        if (values && values.id) return;
        if (!modelId || modelId === lastAppliedModelId) return;

        dataProvider.getOne('subscription-models', { id: modelId })
            .then(({ data }) => {
                console.log('plan', data)
                // On model change, force-sync key properties from the model
                if (data.cycle_limit != null) {
                    form.change('cycle_limit', data.cycle_limit);
                }
                if (data.subscription_period_type) {
                    form.change('subscription_period_type', data.subscription_period_type);
                }
                if (Array.isArray(data.sites)) {
                    form.change('sites', data.sites);
                }
                setLastAppliedModelId(modelId);
            })
            .catch(() => {
                notify('Failed to load model details', 'warning');
            });
    }, [values && values.subscription_model]);

    return null;
};

// Dynamic helper text: only show hint when field is empty
const CycleLimitOverrideInput = () => {
    const { values } = useFormState();
    const showHint = !values || values.cycle_limit == null || values.cycle_limit === '';
    return (
        <NumberInput 
            source="cycle_limit" 
            min={1}
            label="Cycle Limit Override"
            helperText={showHint ? "Leave blank to use model's value" : false}
        />
    );
};

const PeriodTypeOverrideInput = () => {
    const { values } = useFormState();
    const showHint = !values || !values.subscription_period_type;
    return (
        <SelectInput 
            source="subscription_period_type" 
            choices={subscriptionPeriodChoices}
            label="Period Type Override"
            helperText={showHint ? "Leave blank to use model's value" : false}
        />
    );
};

// Auto-calculate renew_date from valid_from and period type
const SubscriptionPlanAutoRenewDate = () => {
    const form = useForm();
    const { values } = useFormState();
    const lastComputedRef = React.useRef(null);

    React.useEffect(() => {
        const validFrom = values && values.valid_from;
        const periodType = (values && values.subscription_period_type) || null;
        if (!validFrom || !periodType) return;

        const computed = computeRenewDate(validFrom, periodType);
        if (!computed) return;

        const currentRenew = values && values.renew_date;
        // Only set if empty or previously auto-computed
        if (!currentRenew || currentRenew === lastComputedRef.current) {
            form.change('renew_date', computed);
            lastComputedRef.current = computed;
        }
    }, [values && values.valid_from, values && values.subscription_period_type]);

    return null;
};

function computeRenewDate(validFrom, periodType) {
    const d = new Date(validFrom);
    if (isNaN(d.getTime())) return null;
    const date = new Date(d.getTime());
    switch (periodType) {
        case 'daily':
            date.setDate(date.getDate() + 1);
            break;
        case 'monthly':
            date.setMonth(date.getMonth() + 1);
            break;
        case 'annually':
            date.setFullYear(date.getFullYear() + 1);
            break;
        default:
            return null;
    }
    return date.toISOString();
}

// Custom toolbar that surfaces server-side validation errors
const SubscriptionPlanFormToolbar = props => {
    const notify = useNotify();
    const extractApiError = (error) => {
        if (!error) return null;
        const body = error.body || error.json || error.data;
        if (typeof body === 'string') return body;
        if (body && typeof body === 'object') {
            if (body.detail) return body.detail;
            // DRF field errors: { field: ["message"] }
            const firstKey = Object.keys(body)[0];
            if (firstKey) {
                const val = body[firstKey];
                if (Array.isArray(val) && val.length > 0) return val[0];
                if (typeof val === 'string') return val;
                try { return JSON.stringify(val); } catch (e) {}
            }
        }
        return error.message || null;
    };
    const handleFailure = (error) => {
        const serverMsg = extractApiError(error) || 'Failed to save';
        notify(serverMsg, 'error');
    };
    return (
        <Toolbar {...props}>
            <SaveButton alwaysEnable onFailure={handleFailure} />
        </Toolbar>
    );
};

const SubscriptionPlanEditActions = props => (
    <TopToolbar {...props} style={{ justifyContent: 'flex-start' }}>
        <ListButton label="Back" icon={<ArrowBackIcon />} />
    </TopToolbar>
);
